/**
 * The status breakdown that sits directly above the All orders table: the
 * figures behind a lane, as compact tiles that also filter the table below.
 *
 *   Deliveries   delivered / part-delivered / out for delivery / at depot /
 *                in transit / removed from plan / departed hub / planned,
 *                then the service-risk tiles
 *   Collections  planned + the four stages, the attention conditions, who
 *                booked them, and collected-without-POC
 *   ETAs         on time / late / delivered without an ETA
 *
 * Every number is the projection's own; nothing is re-bucketed here.
 */

import type {
  CollectionCondition,
  CollectionStage,
  OperationalMetric,
  OrdersDailyStatusBucket,
  OrdersDailySummary,
} from '../../types/ordersDailyTruth';
import {
  COLLECTION_ATTENTION_CONDITIONS,
  COLLECTION_CONDITION_HINTS,
  COLLECTION_CONDITION_LABELS,
  COLLECTION_SOURCE_LABELS,
  COLLECTION_STAGE_HINTS,
  COLLECTION_STAGE_LABELS,
  COLLECTION_STAGE_ORDER,
  DELIVERY_BUCKET_HINTS,
  DELIVERY_BUCKET_LABELS,
  formatConsignments,
  formatLifts,
  formatServiceRiskAtRiskHeadline,
  formatServiceRiskMissedHeadline,
  serviceRiskAtRiskHint,
  serviceRiskMissedHint,
  serviceRiskSectionTitle,
  type OrdersDailyKpiFilter,
} from '../../utils/ordersDailyPresentation';

export type BreakdownLane = 'all' | 'deliveries' | 'collections' | 'etas';

const BUCKET_ORDER: OrdersDailyStatusBucket[] = [
  'delivered',
  'partial',
  'out_for_delivery',
  'at_depot',
  'removed_from_plan',
  'in_transit',
  'exception',
];

const BUCKET_SWATCH: Record<OrdersDailyStatusBucket, string> = {
  delivered: 'bg-success',
  partial: 'bg-chart-9',
  out_for_delivery: 'bg-chart-10',
  at_depot: 'bg-warning',
  in_transit: 'bg-chart-5',
  removed_from_plan: 'bg-chart-2',
  exception: 'bg-destructive',
  unmapped: 'bg-muted-foreground',
};

const COLLECTION_STAGE_SWATCH: Record<CollectionStage, string> = {
  awaiting_collection: 'bg-muted-foreground',
  collected: 'bg-ring',
  at_collection_depot: 'bg-warning',
  dispatched_onward: 'bg-success',
};

const CONDITION_TONE: Record<CollectionCondition, string> = {
  on_track: 'border-border text-foreground',
  at_risk: 'border-warning/50 text-warning',
  overdue: 'border-warning/50 text-warning',
  needs_investigation: 'border-destructive/50 text-destructive',
  expected_exception: 'border-accent/50 text-foreground',
};

function sameFilter(a: OrdersDailyKpiFilter, b: OrdersDailyKpiFilter): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'bucket' && b.kind === 'bucket') return a.bucket === b.bucket && a.direction === b.direction;
  if (a.kind === 'collection_stage' && b.kind === 'collection_stage') return a.stage === b.stage;
  if (a.kind === 'collection_condition' && b.kind === 'collection_condition') return a.condition === b.condition;
  if (a.kind === 'collection_source' && b.kind === 'collection_source') return a.source === b.source;
  return true;
}

function Tile({
  id, label, swatch, count, sub, hint, selected, tone = 'border-border', numberClass = 'text-foreground',
  dashed, disabled, title, onClick,
}: {
  id: string;
  label: string;
  swatch?: string;
  count: string;
  sub?: string;
  hint?: string;
  selected?: boolean;
  tone?: string;
  numberClass?: string;
  dashed?: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      disabled={disabled}
      title={title ?? hint}
      onClick={onClick}
      className={`text-left rounded-lg border ${dashed ? 'border-dashed' : ''} ${tone} bg-card/50 px-3 py-2.5 hover:border-chart-5/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
        selected ? 'ring-2 ring-chart-5/50' : ''
      }`}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {swatch && <span className={`h-2 w-2 shrink-0 rounded-sm ${swatch}`} aria-hidden />}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-3xl font-bold tabular-nums leading-none ${numberClass}`}>{count}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground tabular-nums">{sub}</div>}
    </button>
  );
}

const metricSub = (m: OperationalMetric | undefined): string | undefined =>
  m ? `${formatLifts(m.lifts)} lifts` : undefined;

export interface OrdersBreakdownStripProps {
  summary: OrdersDailySummary | null | undefined;
  lane: BreakdownLane;
  onLane: (lane: BreakdownLane) => void;
  tableFilter: OrdersDailyKpiFilter;
  /** Show these rows in the table below. */
  onFilter: (filter: OrdersDailyKpiFilter) => void;
  startDate: string;
  endDate: string;
  viewingLondonToday: boolean;
  rangeIncludesLondonToday: boolean;
}

const LANE_TABS: Array<{ id: BreakdownLane; label: string; filter: OrdersDailyKpiFilter }> = [
  { id: 'all', label: 'All legs', filter: { kind: 'all' } },
  { id: 'deliveries', label: 'Deliveries', filter: { kind: 'deliveries' } },
  { id: 'collections', label: 'Collections', filter: { kind: 'collections' } },
  { id: 'etas', label: 'ETAs', filter: { kind: 'eta' } },
];

export default function OrdersBreakdownStrip({
  summary, lane, onLane, tableFilter, onFilter, startDate, endDate, viewingLondonToday, rangeIncludesLondonToday,
}: OrdersBreakdownStripProps) {
  const counts: Record<BreakdownLane, number | undefined> = {
    all: summary?.row_count,
    deliveries: summary?.deliveries.total.consignments,
    collections: summary?.collections.total.consignments,
    etas: summary?.eta?.total.consignments,
  };
  const partial = summary?.partial_deliveries;
  const risk = summary?.service_risk;

  return (
    <div className="space-y-3" aria-label="Breakdown">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Which orders to break down">
        {LANE_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            aria-pressed={lane === tab.id}
            onClick={() => { onLane(tab.id); onFilter(tab.filter); }}
            className={`rounded-md border px-3 py-1 text-xs font-medium ${
              lane === tab.id
                ? 'border-chart-5/60 bg-card text-white'
                : 'border-border text-foreground hover:bg-card/70'
            }`}
          >
            {tab.label}
            {counts[tab.id] != null ? ` ${counts[tab.id]!.toLocaleString()}` : ''}
          </button>
        ))}
      </div>

      {lane === 'deliveries' && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {rangeIncludesLondonToday && (
              <Tile
                id="kpi-bucket-planned"
                label="Planned, no due date yet"
                swatch="bg-chart-5"
                dashed
                numberClass="text-chart-5"
                count={summary.unscheduled.deliveries.consignments.toLocaleString()}
                sub={metricSub(summary.unscheduled.deliveries)}
                hint="Entered by Palletways, not yet given a due date. Outside every other group here."
                onClick={() => onFilter({ kind: 'deliveries' })}
              />
            )}
            {BUCKET_ORDER.map((bucket) => {
              const metric = summary.delivery_buckets[bucket];
              if (!metric) return null;
              const isException = bucket === 'exception';
              const sub = bucket === 'partial' && partial && partial.consignments > 0
                ? `${formatLifts(partial.lifts_delivered)} lifts dropped, ${formatLifts(partial.lifts_outstanding)} still out`
                : metricSub(metric);
              return (
                <Tile
                  key={bucket}
                  id={`kpi-bucket-${bucket}`}
                  label={DELIVERY_BUCKET_LABELS[bucket]}
                  swatch={BUCKET_SWATCH[bucket]}
                  tone={isException ? 'border-destructive/50' : 'border-border'}
                  numberClass={isException && metric.consignments > 0 ? 'text-destructive' : 'text-foreground'}
                  count={metric.consignments.toLocaleString()}
                  sub={sub}
                  hint={DELIVERY_BUCKET_HINTS[bucket]}
                  selected={sameFilter(tableFilter, { kind: 'bucket', bucket, direction: 'delivery' })}
                  onClick={() => onFilter({ kind: 'bucket', bucket, direction: 'delivery' })}
                />
              );
            })}
          </div>

          <h4 className="pt-1 text-xs font-semibold uppercase tracking-wide-3 text-muted-foreground">
            {serviceRiskSectionTitle(startDate, endDate)}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(() => {
              const missedCodes = (risk?.missed_am_codes?.length ? risk.missed_am_codes : ['E']).join('/');
              const atRiskGroup = risk?.at_risk_service_group || 'PREMIUM';
              const atRiskTime = (risk?.at_risk_due_time || '17:00').slice(0, 5);
              const atRiskInactive = !rangeIncludesLondonToday;
              const missedC = risk?.missed_am.consignments ?? 0;
              const atRiskC = atRiskInactive ? 0 : (risk?.at_risk_1700.consignments ?? 0);
              return (
                <>
                  <Tile
                    id="risk-missed"
                    label="Missed by 12:00"
                    tone="border-destructive/40"
                    numberClass="text-destructive"
                    count={formatServiceRiskMissedHeadline(risk?.missed_am.impact_gbp ?? null, missedC)}
                    sub={`${missedC} consignments · ${formatLifts(risk?.missed_am.lifts ?? 0)} lifts · service ${missedCodes}`}
                    hint={serviceRiskMissedHint({
                      consignments: missedC,
                      impactGbp: risk?.missed_am.impact_gbp ?? null,
                      missedCodesLabel: missedCodes,
                      viewingLondonToday,
                    })}
                    selected={sameFilter(tableFilter, { kind: 'missed_am' })}
                    onClick={() => onFilter({ kind: 'missed_am' })}
                  />
                  <Tile
                    id="risk-at-risk"
                    label="At risk by 17:00"
                    tone="border-warning/40"
                    numberClass={atRiskInactive ? 'text-muted-foreground' : 'text-warning'}
                    count={atRiskInactive
                      ? 'n/a'
                      : formatServiceRiskAtRiskHeadline(
                        risk?.at_risk_1700.impact_gbp_min ?? null,
                        risk?.at_risk_1700.impact_gbp_max ?? null,
                        atRiskC,
                      )}
                    sub={atRiskInactive
                      ? `${atRiskGroup} by ${atRiskTime} · today only`
                      : `${formatLifts(risk?.at_risk_1700.lifts ?? 0)} lifts still open · ${atRiskGroup} by ${atRiskTime}`}
                    hint={serviceRiskAtRiskHint({
                      consignments: atRiskC,
                      impactGbpMin: atRiskInactive ? null : (risk?.at_risk_1700.impact_gbp_min ?? null),
                      atRiskTime,
                      rangeIncludesLondonToday,
                    })}
                    disabled={atRiskInactive}
                    title={atRiskInactive ? 'At risk only applies when the period includes London today' : undefined}
                    selected={sameFilter(tableFilter, { kind: 'at_risk_1700' })}
                    onClick={() => onFilter({ kind: 'at_risk_1700' })}
                  />
                  <Tile
                    id="risk-booked"
                    label="Booked in, excluded"
                    tone="border-accent/50"
                    numberClass="text-foreground"
                    count={String(risk?.booked_in_excluded.consignments ?? 0)}
                    sub={`${formatLifts(risk?.booked_in_excluded.lifts ?? 0)} lifts · delivery slot agreed`}
                    hint="BookedIn = yes. No AM or evening fee applies"
                    selected={sameFilter(tableFilter, { kind: 'booked_in_excluded' })}
                    onClick={() => onFilter({ kind: 'booked_in_excluded' })}
                  />
                </>
              );
            })()}
          </div>
        </>
      )}

      {lane === 'collections' && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {rangeIncludesLondonToday && (
              <Tile
                id="kpi-collection-bucket-planned"
                label="Planned, no date yet"
                swatch="bg-chart-5"
                dashed
                numberClass="text-chart-5"
                count={summary.unscheduled.collections.consignments.toLocaleString()}
                sub={metricSub(summary.unscheduled.collections)}
                hint="Booked but never captured an entered or manifest date."
                onClick={() => onFilter({ kind: 'collections' })}
              />
            )}
            {COLLECTION_STAGE_ORDER.map((stage) => {
              const metric = summary.collection_stages?.[stage];
              return (
                <Tile
                  key={stage}
                  id={`kpi-collection-stage-${stage}`}
                  label={COLLECTION_STAGE_LABELS[stage]}
                  swatch={COLLECTION_STAGE_SWATCH[stage]}
                  count={metric ? metric.consignments.toLocaleString() : 'n/a'}
                  sub={metricSub(metric)}
                  hint={COLLECTION_STAGE_HINTS[stage]}
                  selected={sameFilter(tableFilter, { kind: 'collection_stage', stage })}
                  onClick={() => onFilter({ kind: 'collection_stage', stage })}
                />
              );
            })}
          </div>

          {summary.collection_conditions && (
            <div className="flex flex-wrap gap-2" aria-label="Collections needing attention">
              {COLLECTION_ATTENTION_CONDITIONS.map((condition) => {
                const metric = summary.collection_conditions![condition];
                const active = metric.consignments > 0;
                return (
                  <button
                    key={condition}
                    type="button"
                    id={`kpi-collection-condition-${condition}`}
                    disabled={!active}
                    title={COLLECTION_CONDITION_HINTS[condition]}
                    onClick={() => onFilter({ kind: 'collection_condition', condition })}
                    className={`rounded-full border px-3 py-1 text-xs disabled:cursor-default ${
                      active ? CONDITION_TONE[condition] : 'border-border text-muted-foreground'
                    } ${sameFilter(tableFilter, { kind: 'collection_condition', condition }) ? 'ring-2 ring-chart-5/50' : ''}`}
                  >
                    {COLLECTION_CONDITION_LABELS[condition]}{' '}
                    <span className="font-semibold tabular-nums">{metric.consignments.toLocaleString()}</span>
                  </button>
                );
              })}
            </div>
          )}

          {summary.collection_sources && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="uppercase tracking-wide-3 text-xs font-semibold text-muted-foreground">Booked by</span>
              {(['own_customer', 'network', 'unknown'] as const).map((source) => {
                const metric = summary.collection_sources![source];
                if (source === 'unknown' && metric.consignments === 0) return null;
                return (
                  <button
                    key={source}
                    type="button"
                    id={`kpi-collection-source-${source}`}
                    onClick={() => onFilter({ kind: 'collection_source', source })}
                    className={`hover:text-foreground ${sameFilter(tableFilter, { kind: 'collection_source', source }) ? 'text-chart-5 underline' : ''}`}
                    title={source === 'unknown'
                      ? 'Palletways booking detail not read for these yet. Older closed jobs stay unknown'
                      : undefined}
                  >
                    {COLLECTION_SOURCE_LABELS[source]}{' '}
                    <span className="font-semibold text-foreground tabular-nums">{metric.consignments.toLocaleString()}</span>
                  </button>
                );
              })}
            </div>
          )}

          {(summary.collections_without_poc?.consignments ?? 0) > 0 && (
            <button
              type="button"
              id="kpi-collection-without-poc"
              onClick={() => onFilter({ kind: 'collection_without_poc' })}
              className={`w-full text-left rounded-lg border border-border bg-card/40 px-3 py-2 text-xs text-foreground hover:border-chart-5/40 ${
                tableFilter.kind === 'collection_without_poc' ? 'ring-2 ring-chart-5/50' : ''
              }`}
            >
              <span className="font-medium text-foreground">
                Collected without a POC: {formatConsignments(summary.collections_without_poc!.consignments)}
              </span>
              {' '}· {formatLifts(summary.collections_without_poc!.lifts)} lifts. No Proof of Collection on record, but a depot
              or trunk scan shows the goods were collected.
            </button>
          )}
        </>
      )}

      {lane === 'etas' && summary && (
        summary.eta ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Tile
              id="kpi-eta-on-time"
              label="On time"
              swatch="bg-success"
              count={summary.eta.on_time.consignments.toLocaleString()}
              sub={metricSub(summary.eta.on_time)}
              hint="Delivered inside the Palletways delivery window"
              onClick={() => onFilter({ kind: 'eta' })}
            />
            <Tile
              id="kpi-eta-late"
              label="Late, delivered"
              swatch="bg-destructive"
              tone={summary.eta.late.consignments > 0 ? 'border-destructive/50' : 'border-border'}
              numberClass={summary.eta.late.consignments > 0 ? 'text-destructive' : 'text-foreground'}
              count={summary.eta.late.consignments.toLocaleString()}
              sub={metricSub(summary.eta.late)}
              hint="Delivered after the delivery window ended"
              selected={sameFilter(tableFilter, { kind: 'eta_late' })}
              onClick={() => onFilter({ kind: 'eta_late' })}
            />
            <Tile
              id="kpi-eta-excluded"
              label="Delivered, no ETA"
              swatch="bg-muted-foreground"
              dashed
              count={Math.max(0, summary.deliveries.completed.consignments - summary.eta.total.consignments).toLocaleString()}
              hint="Delivered without a Palletways ETA estimate, so left out of the percentage"
              onClick={() => onFilter({ kind: 'deliveries' })}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No ETA estimates were received for this period.</p>
        )
      )}
    </div>
  );
}
